import React from "react";
import styled, { css } from "styled-components";
import { useMappedState } from "redux-react-hook";
import { useHistory } from "react-router-dom";

import { Table } from "src/core/dashboard/components/widgets/index";
import {
  FilterParams,
  ITerminology,
  PaginatedParams
} from "src/library/utils";
import {
  IOrder,
  ListOrdersByDropParams,
  ordersApi,
  OrdersResponse
} from "src/core/orders/api/orders.api";
import { Button } from "src/library/components/Button";
import { IStore } from "src/core/shared/store";
import { OrderActionsList } from "src/core/orders/components/OrderActionsList";
import { ITableInstance } from "src/library/components/Tables/Table";
import { Icon } from "src/library/components/Icon";
import { Icons } from "src/library/components/Icon/types";
import { Tooltip } from "src/library/components/Tooltip";
import { Text } from "src/library/components/Text";
import {
  OrderAvailableTableColumn,
  OrderTableNonFilterableColumns,
  getOrderTableColumns
} from "src/core/shared/components/tables/ordersTableColumns";

export interface DropOrdersTable {
  dropId: number;
  terminology: ITerminology;
  selectedColumns?: OrderAvailableTableColumn[];
  nonFilterableColumns?: OrderTableNonFilterableColumns;
  customCallback?: (order: IOrder) => void;
  requiredFilters?: FilterParams<IOrder>;
}

const defaultPagination: PaginatedParams<IOrder> = {
  page: 0,
  limit: 10,
  offset: 0,
  sort: [ [ "updatedAt", "DESC" ] ]
};

/**
 * A table displaying drops orders with the ability to render specified columns
 */
const DropOrdersTable: React.FunctionComponent<DropOrdersTable> = ({
  dropId, terminology, customCallback, requiredFilters, selectedColumns, nonFilterableColumns
}) => {
  const [ ordersResponse, getOrders ] = ordersApi<OrdersResponse>("LIST_DROP_ORDERS");
  const history = useHistory();
  const TableRef = React.useRef<ITableInstance<IOrder>>(null);
  const ordersData = React.useMemo(() => ordersResponse.data, [ ordersResponse.data ]);

  const {
    orderStatuses, role, erp, installedModules
  } = useMappedState((store: IStore) => ({
    orderStatuses: store.global.settings ? store.global.settings.orderStatuses : [],
    role: store.global.role,
    erp: store.global.settings && store.global.settings.erp.name,
    installedModules: store.global.settings?.installedModules || []
  }));

  const onFetchData = React.useCallback(
    (params: { filters: FilterParams<IOrder>; pagination: PaginatedParams<IOrder>;}) => {
      const { orderStatus, ...filters } = params.filters;

      getOrders<ListOrdersByDropParams>({
        pagination: params.pagination,
        filters: {
          ...filters,
          orderStatusId: orderStatus ? parseInt(orderStatus, 10) : undefined,
          ...requiredFilters || {}
        },
        dropId
      });
    }, [ dropId, requiredFilters ]);

  const reload = React.useCallback(() => {
    if (TableRef.current) {
      TableRef.current.reloadTable();
    }
  }, [ TableRef ]);

  React.useEffect(() => {
    // reload table every time dropId or required filters changes
    reload();
  }, [
    dropId,
    reload,
    requiredFilters
  ]);

  const tableColumns = React.useMemo(() => {
    if (!erp || !installedModules || !orderStatuses || !role || !terminology) {
      return [];
    }

    return getOrderTableColumns({
      installedModules,
      terminology,
      orderStatuses,
      pricesVisible: false,
      selectedColumns,
      nonFilterableColumns,
      actionButton: info => {
        if (customCallback) {
          const iconKey = `icon-${info.row.original.id}`;

          return (
            <div className="custom-callback">
              <Button onClick={() => customCallback(info.row.original)}>
                Select
              </Button>

              <Icon
                icon={Icons.OPENNEW}
                onClick={() => window.open(`/orders/${info.row.original.id}`)}
                data-tip
                data-for={iconKey}
              />

              <Tooltip
                id={iconKey}
                type="dark"
                place="right"
                getContent={() => {
                  return (
                    <Text color="white">
                      Open order in new tab
                    </Text>
                  );
                }}
              />
            </div>
          );
        }

        if (dropId && erp && role) {
          return (
            <OrderActionsList
              erp={erp}
              terminology={terminology}
              role={role}
              order={info.row.original}
              reload={reload}
            />
          );
        }

        return null;
      }
    });
  }, [
    erp,
    installedModules,
    orderStatuses,
    role,
    terminology,
    customCallback,
    dropId,
    reload
  ]);

  return (
    <StyledWrapper>

      <Table
        tableOf={terminology.order.plural.text}
        className="large"
        data={ordersData?.orders.result || []}
        onFetchData={onFetchData}
        defaultPagination={defaultPagination}
        loading={ordersResponse.pending}
        pageCount={ordersData ? ordersData.orders.pages : 0}
        rowCount={ordersData ? ordersData.orders.count : 0}
        error={ordersResponse.error}
        columns={tableColumns}
        ref={TableRef}
        mainAction={row => history.push(`/orders/${row.original.id}`)}
      />

    </StyledWrapper>
  );
};

const StyledWrapper = styled.span`
  ${({ theme: _t }) => css`
    .custom-callback {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;

      svg {
        margin-left: ${_t.spacing.margins.sm};

        &:hover {
          cursor: pointer;
        }
      }
    }
  `}
`;

export default DropOrdersTable;
